import React, { useState, useRef, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import config from '../config';

const stripePromise = config.STRIPE_PUBLIC_KEY ? loadStripe(config.STRIPE_PUBLIC_KEY) : null;

function mapCartItems(cart) {
  return cart.map((item) => ({
    productId: item.id,
    size: item.selectedSize || 'One Size',
    quantity: item.quantity || 1,
  }));
}

function buildCustomerPayload({ name, email, address, city, state, zip, country, secondaryAddress }) {
  const fullAddress = secondaryAddress ? `${address}, ${secondaryAddress}` : address;
  return { name, email, address: fullAddress, city, state, zip, country };
}

function ShopPayButton({ cart, discountCode, customer, onComplete, onError, country, total }) {
  const stripe = useStripe();
  const elements = useElements();
  const paymentRequestButtonRef = useRef(null);
  const paymentRequestElementRef = useRef(null);
  const paymentRequestRef = useRef(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentRequest = stripe.paymentRequest({
      country: country === 'United States' ? 'US' : 'CA',
      currency: 'usd',
      total: { label: 'Total', amount: Math.round(total * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    paymentRequest.on('paymentmethod', async (ev) => {
      try {
        const intentRes = await fetch(`${config.API_BASE}/api/checkout/create-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ items: mapCartItems(cart), discountCode: discountCode || undefined }),
        });
        const intentData = await intentRes.json();
        if (!intentData.success) throw new Error(intentData.error || 'Failed to create payment');

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          intentData.clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          onError(confirmError.message);
          return;
        }

        ev.complete('success');
        await onComplete(paymentIntent?.id || intentData.paymentIntentId);
      } catch (err) {
        ev.complete('fail');
        onError(err.message);
      }
    });

    paymentRequest.canMakePayment().then((result) => {
      if (result) {
        setCanMakePayment(true);
        paymentRequestRef.current = paymentRequest;
      }
    });

    return () => {
      paymentRequestElementRef.current = null;
      paymentRequestRef.current = null;
    };
  }, [stripe, elements, total, country, cart, discountCode, customer, onComplete, onError]);

  useEffect(() => {
    if (canMakePayment && paymentRequestButtonRef.current && paymentRequestRef.current && elements && !paymentRequestElementRef.current) {
      const paymentRequestButton = elements.create('paymentRequestButton', {
        paymentRequest: paymentRequestRef.current,
        style: { paymentRequestButton: { theme: 'dark', height: '48px' } },
      });
      paymentRequestButton.mount(paymentRequestButtonRef.current);
      paymentRequestElementRef.current = paymentRequestButton;
    }
  }, [canMakePayment, elements]);

  if (!canMakePayment) return null;
  return <div ref={paymentRequestButtonRef} style={{ marginBottom: 18 }} />;
}

function StripeCheckoutForm({ cart, discountCode, customer, total, onComplete, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    onError(null);
    if (!stripe || !elements) {
      setLoading(false);
      return;
    }
    try {
      const intentRes = await fetch(`${config.API_BASE}/api/checkout/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: mapCartItems(cart), discountCode: discountCode || undefined }),
      });
      const intentData = await intentRes.json();
      if (!intentData.success) throw new Error(intentData.error || 'Failed to create payment');

      const result = await stripe.confirmCardPayment(intentData.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        onError(result.error.message);
      } else if (result.paymentIntent?.status === 'succeeded') {
        await onComplete(result.paymentIntent.id);
      } else {
        onError('Payment failed.');
      }
    } catch (err) {
      onError(err.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
      <div style={{ background: '#fff', borderRadius: 6, padding: 22, border: '1.5px solid #e0e0e0', marginBottom: 24, width: '100%' }}>
        <h3 style={{ textAlign: 'left', marginBottom: 8 }}>Pay with Card</h3>
        <CardElement options={{ style: { base: { fontSize: '18px' } } }} />
      </div>
      <button type="submit" style={{ background: '#000', color: '#fff', fontSize: 18, padding: '14px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 'bold', width: '100%' }} disabled={loading || !stripe}>
        {loading ? 'Processing...' : `Pay $${total.toFixed(2)}`}
      </button>
    </form>
  );
}

export default function Checkout({ cart = [], onChangeQuantity, onRemoveFromCart, onClearCart }) {
  const savedInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem('customerInfo')) || {};
    } catch {
      return {};
    }
  })();

  const [name, setName] = useState(savedInfo.name || '');
  const [address, setAddress] = useState(savedInfo.address || '');
  const [secondaryAddress, setSecondaryAddress] = useState(savedInfo.secondaryAddress || '');
  const [city, setCity] = useState(savedInfo.city || '');
  const [state, setState] = useState(savedInfo.state || '');
  const [zip, setZip] = useState(savedInfo.zip || '');
  const [country, setCountry] = useState(savedInfo.country || 'United States');
  const [email, setEmail] = useState(savedInfo.email || '');
  const [stickyLeft, setStickyLeft] = useState(0);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const customerInfoRef = useRef(null);
  const mainRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const [orderResult, setOrderResult] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const [discount, setDiscount] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [serverTotals, setServerTotals] = useState(null);

  useEffect(() => {
    localStorage.setItem('customerInfo', JSON.stringify({
      name, address, secondaryAddress, city, state, zip, country, email,
    }));
  }, [name, address, secondaryAddress, city, state, zip, country, email]);

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discountAmount = serverTotals?.discountAmount ?? 0;
  const total = serverTotals?.total ?? subtotal;

  const refreshServerTotals = useCallback(async (code) => {
    if (cart.length === 0) {
      setServerTotals(null);
      return;
    }
    try {
      const res = await fetch(`${config.API_BASE}/api/checkout/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: mapCartItems(cart), discountCode: code || undefined, previewOnly: true }),
      });
      const data = await res.json();
      if (data.success && data.totals) {
        setServerTotals(data.totals);
        setDiscountError('');
      } else if (code) {
        setAppliedDiscountCode('');
        setServerTotals(null);
        setDiscountError(data.error || 'Invalid discount code');
      }
    } catch {
      setDiscountError('Could not validate discount code');
    }
  }, [cart]);

  useEffect(() => {
    refreshServerTotals(appliedDiscountCode);
  }, [cart, appliedDiscountCode, refreshServerTotals]);

  useEffect(() => {
    function updateStickyPosition() {
      if (mainRef.current && customerInfoRef.current) {
        const mainRect = mainRef.current.getBoundingClientRect();
        setStickyLeft(mainRect.left + mainRect.width + 10 + window.scrollX);
      }
    }
    updateStickyPosition();
    window.addEventListener('resize', updateStickyPosition);
    window.addEventListener('scroll', updateStickyPosition);
    return () => {
      window.removeEventListener('resize', updateStickyPosition);
      window.removeEventListener('scroll', updateStickyPosition);
    };
  }, []);

  const customerPayload = buildCustomerPayload({ name, email, address, city, state, zip, country, secondaryAddress });

  const completeCheckout = async (paymentMethod, paymentRef) => {
    if (!name || !email || !address || !city || !state || !zip) {
      setOrderError('Please complete all required customer and shipping fields.');
      return;
    }
    const body = {
      paymentMethod,
      customer: customerPayload,
      items: mapCartItems(cart),
      discountCode: appliedDiscountCode || undefined,
    };
    if (paymentMethod === 'stripe') body.paymentIntentId = paymentRef;
    if (paymentMethod === 'paypal') body.paypalOrderId = paymentRef;

    const response = await fetch(`${config.API_BASE}/api/checkout/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (result.success) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: result.order.order_number,
          currency: 'USD',
          value: total,
        });
      }
      setOrderResult({ order: result.order });
      if (onClearCart) onClearCart();
    } else {
      setOrderError(result.error || 'Payment successful but order creation failed');
    }
  };

  const handleStripeComplete = (paymentIntentId) => completeCheckout('stripe', paymentIntentId);
  const handleStripeError = (msg) => setOrderError(msg);

  function applyDiscount() {
    const code = discount.trim().toUpperCase();
    if (!code) {
      setAppliedDiscountCode('');
      setDiscountError('');
      setServerTotals(null);
      return;
    }
    setAppliedDiscountCode(code);
    refreshServerTotals(code);
  }

  const countryOptions = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan'];

  if (!stripePromise && !config.PAYPAL_CLIENT_ID) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Payment is not configured.</div>;
  }

  return (
    <div className="checkout" style={{ position: 'relative', width: '1100px', zIndex: 2001, margin: '0 auto', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', background: '#fff', minHeight: 600 }}>
      <div ref={mainRef} style={{ width: 700, background: '#fff', borderRadius: 6, padding: 24, paddingTop: 0, minHeight: 500, boxSizing: 'border-box' }}>
        {stripePromise && (
          <Elements stripe={stripePromise}>
            <ShopPayButton
              cart={cart}
              discountCode={appliedDiscountCode}
              customer={customerPayload}
              total={total}
              country={country}
              onComplete={handleStripeComplete}
              onError={handleStripeError}
            />
          </Elements>
        )}

        {total > 0 && config.PAYPAL_CLIENT_ID && (
          <div style={{ marginBottom: 18 }}>
            <PayPalScriptProvider options={{ 'client-id': config.PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}>
              <PayPalButtons
                style={{ layout: 'horizontal', color: 'black', shape: 'rect', label: 'paypal', height: 48 }}
                forceReRender={[total, appliedDiscountCode]}
                createOrder={async () => {
                  const res = await fetch(`${config.API_BASE}/api/checkout/create-paypal-order`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                      items: mapCartItems(cart),
                      discountCode: appliedDiscountCode || undefined,
                    }),
                  });
                  const data = await res.json();
                  if (!data.success || !data.paypalOrderId) {
                    throw new Error(data.error || 'Failed to create PayPal order');
                  }
                  if (data.totals) setServerTotals(data.totals);
                  return data.paypalOrderId;
                }}
                onApprove={async (data, actions) => {
                  try {
                    await actions.order.capture();
                    await completeCheckout('paypal', data.orderID);
                  } catch (error) {
                    setOrderError(error.message || 'PayPal checkout failed');
                  }
                }}
                onError={(err) => setOrderError(String(err))}
              />
            </PayPalScriptProvider>
          </div>
        )}

        <div ref={customerInfoRef} style={{ background: '#fff', borderRadius: 6, padding: 22, border: '1.5px solid #e0e0e0', marginBottom: 24 }}>
          <h2 style={{ fontSize: 32, fontWeight: 700, margin: 0 }}>Customer Info</h2>
          <label>Email *</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" style={{ width: '100%', fontSize: 18, padding: 10, marginBottom: 12 }} />
        </div>

        <div style={{ background: '#fff', borderRadius: 6, padding: 22, border: '1.5px solid #e0e0e0', marginBottom: 24 }}>
          <h3>Shipping Information</h3>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name" required style={{ width: '100%', marginBottom: 8, padding: 10 }} />
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" required style={{ width: '100%', marginBottom: 8, padding: 10 }} />
          <input value={secondaryAddress} onChange={(e) => setSecondaryAddress(e.target.value)} placeholder="Secondary Address" style={{ width: '100%', marginBottom: 8, padding: 10 }} />
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required style={{ width: '100%', marginBottom: 8, padding: 10 }} />
          <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" required style={{ width: '100%', marginBottom: 8, padding: 10 }} />
          <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP" required style={{ width: '100%', marginBottom: 8, padding: 10 }} />
          <select value={country} onChange={(e) => setCountry(e.target.value)} style={{ width: '100%', padding: 10 }}>
            {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {stripePromise && (
          <Elements stripe={stripePromise}>
            <StripeCheckoutForm
              cart={cart}
              discountCode={appliedDiscountCode}
              customer={customerPayload}
              total={total}
              onComplete={handleStripeComplete}
              onError={handleStripeError}
            />
          </Elements>
        )}
      </div>

      <div style={{ position: 'fixed', top: 90, left: stickyLeft, width: 360, zIndex: 2001, background: '#fff' }}>
        <div style={{ background: '#fff', borderRadius: 6, padding: 28, border: '2px solid #bdbdbd' }}>
          <h3>Order Summary</h3>
          {cart.length === 0 ? <p>Your cart is empty.</p> : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {cart.map((item) => (
                <li key={item.id + (item.selectedSize || '')} style={{ marginBottom: 8 }}>
                  {item.name} {item.selectedSize && `(${item.selectedSize})`} x{item.quantity} — ${item.price.toFixed(2)}
                  <button onClick={() => onRemoveFromCart(item.id)} style={{ marginLeft: 8 }}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <div>Subtotal: ${subtotal.toFixed(2)}</div>
          {discountAmount > 0 && <div>Discount: -${discountAmount.toFixed(2)}</div>}
          <div style={{ fontWeight: 'bold' }}>Total: ${total.toFixed(2)}</div>
        </div>
        <div style={{ marginTop: 16, padding: 22, border: '1.5px solid #e0e0e0' }}>
          <h3>Discount Code</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="Enter code" style={{ flex: 1, padding: 10 }} />
            <button type="button" onClick={applyDiscount}>Apply</button>
          </div>
          {appliedDiscountCode && <div>Code <b>{appliedDiscountCode}</b> applied</div>}
          {discountError && <div style={{ color: '#b91c1c' }}>{discountError}</div>}
        </div>
      </div>

      {orderResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ background: '#fff', padding: 40, borderRadius: 12, textAlign: 'center' }}>
            <h2>Thank you for your purchase!</h2>
            <p>Order: <b>{orderResult.order.order_number}</b></p>
            <button onClick={() => setOrderResult(null)}>Close</button>
          </div>
        </div>
      )}
      {orderError && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ background: '#fff', padding: 40, borderRadius: 12, textAlign: 'center' }}>
            <h2 style={{ color: '#b91c1c' }}>Order Error</h2>
            <p>{orderError}</p>
            <button onClick={() => setOrderError(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
